import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import MaterialItem from "./CustomerQuoteMaterialItem";
import LaborItem from "./CustomerQuoteLaborItem";
import MiscItem from "./CustomerQuoteMiscItem";
import CustomerTerms from "./CustomerTerms";
import DocumentRef from "./DocumentRef";
import { BillTo, ShipTo } from "./CustomerQuote";
import Total, { computeTotal } from "./InvoiceTotal";
import { INVOICE_STATUS_TYPES } from "./Invoice";
import { deepFreeze, isDue, timestampToDate } from "../util/misc";
import COLORS from "../colors";
import { LOCATION_INFO } from "../constants";
import { sortBy, uniq } from "lodash";

export const CUSTOMER_INVOICE_FIELDS = {
  projectID: "",
  saleID: "",
  location: null,
  billTo: null,
  shipTo: null,
  items: [],
  notax: false,
  tax: null,
  terms: null,
  total: null,
  invoiceDate: null,
  dueDate: null,
  document: null,
  voidedDate: null,
  voidedReason: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(CUSTOMER_INVOICE_FIELDS);

export default class CustomerInvoice extends DataStructure {
  constructor(customerID, id, data = {}) {
    data = sanitize(data, CUSTOMER_INVOICE_FIELDS);

    // Convert to Date
    data.invoiceDate = timestampToDate(data.invoiceDate);
    data.dueDate = timestampToDate(data.dueDate);
    data.voidedDate = timestampToDate(data.voidedDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.customerID = customerID;
    this.id = id;

    // Defaults
    this.data.billTo = this.billTo.data;
    this.data.shipTo = this.shipTo.data;
    this.data.terms = this.terms.data;
    this.data.total = this.total.data;

    // Document
    if (this.data.document) {
      this.data.document = new DocumentRef(this.data.document).data;
    }
  }

  get expired() {
    return isDue(this.data.status.received.date, -30);
  }

  // Database

  get path() {
    return CustomerInvoice.getPath(this.customerID, this.id);
  }

  get responseID() {
    const date = this.data.status.received.date;
    return date ? String(date.getTime()) : null;
  }

  static getPath(customerID, id) {
    let path = `c/${customerID}/invoices`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(invoice) {
    return invoice.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new CustomerInvoice(
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  setItemLines() {
    this.data.items.forEach((item, i) => {
      item.line = i + 1;
    });
  }

  computeTotal(items) {
    return computeTotal({ items, tax: this.data.tax });
  }

  // Formatting

  get location() {
    return this.data.location ? LOCATION_INFO[this.data.location] : null;
  }

  get billTo() {
    return this.data.billTo ? new BillTo(this.data.billTo) : new BillTo();
  }

  get shipTo() {
    return this.data.shipTo ? new ShipTo(this.data.shipTo) : new ShipTo();
  }

  get total() {
    return new Total(this.data.total);
  }

  get sortedCategories() {
    const categories = uniq(
      this.data.items.map((item) => item.category || "Other")
    );
    if (categories.length > 1 || categories[0] !== "Other") {
      return categories;
    }
    return [];
  }

  get terms() {
    return new CustomerTerms(this.data.terms);
  }

  get document() {
    return new DocumentRef(this.data.document);
  }

  get items() {
    return sortBy(
      this.data.items.map((item) => {
        switch (item.itemType) {
          case "misc":
            return new MiscItem(item);
          case "labor":
            return new LaborItem(item);
          case "material":
          default:
            return new MaterialItem(item);
        }
      }),
      "line"
    );
  }

  get status() {
    if (this.data.voidedDate) {
      return "voided";
    } else if (!this.data.total.balance) {
      return "paid";
    } else if (isDue(this.data.dueDate)) {
      return "due";
    } else if (isDue(this.data.dueDate, 7)) {
      return "near-due";
    } else {
      return "unpaid";
    }
  }

  get statusType() {
    const type = INVOICE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy] || this.data.createdBy
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy] || this.data.modifiedBy
      : null;
  }
}
